<template>
  <v-dialog v-model="dialog" v-if="billing" max-width="650">
    <Loading :loading="isLoading" />

    <!-- Activator -->
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="primary" dark v-bind="attrs" v-on="on">Change Plan</v-btn>
    </template>

    <v-card outlined class="rounded-xl">
      <CardToolbar title="Change Plan" :isModal="true" @cancel="cancel" />

      <div class="text-center mt-3 mb-6 subtitle-2 grey--text">
        All plan changes are prorated and will apply immediately.
      </div>

      <!-- ✅ Plan Grid -->
      <v-container fluid>
        <v-row>
          <v-col
            v-for="plan in plans"
            :key="plan.id"
            cols="12"
            sm="6"
            md="4"
            class="d-flex"
          >
            <v-card
              outlined
              class="plan-card flex-grow-1"
              :elevation="plan.planId === billing.basePlanId ? 8 : 2"
              :class="{ 'active-plan': plan.planId === billing.basePlanId }"
            >
              <!-- 🟢 Current Plan Chip -->
              <div
                v-if="plan.planId === billing.basePlanId"
                class="text-center mt-3"
              >
                <v-chip
                  color="success"
                  text-color="white"
                  small
                  class="font-weight-medium mb-2 current-chip"
                >
                  <v-icon left small>mdi-check-circle</v-icon>
                  Current Plan
                </v-chip>
              </div>

              <v-card-text class="text-center">
                <h3 class="font-weight-bold mb-1">{{ plan.name }}</h3>

                <div class="display-1 font-weight-bold mb-2">
                  {{
                    parseFloat(plan.amount) > 0
                      ? $options.filters.currency(plan.amount)
                      : "Free"
                  }}
                  <span
                    v-if="parseFloat(plan.amount) > 0"
                    class="subtitle-2 grey--text text--darken-1"
                    >/month</span
                  >
                </div>

                <div
                  v-if="plan.planId !== billing.basePlanId"
                  class="caption grey--text mb-4"
                >
                  First time subscribers receive
                  <strong>{{ plan.trialDays }}</strong> days trial. Cancel
                  anytime.
                </div>

                <!-- ✅ Choose Plan button with confirmation -->
                <v-btn
                  v-if="plan.planId !== billing.basePlanId && !isFreePlan(plan)"
                  color="primary"
                  rounded
                  depressed
                  @click="confirmPlanChange(plan)"
                >
                  Choose Plan
                </v-btn>

                <!-- Disable Free Plan selection -->
                <v-chip
                  v-else-if="
                    isFreePlan(plan) && plan.planId !== billing.basePlanId
                  "
                  small
                  color="grey lighten-2"
                  text-color="grey darken-1"
                  class="font-weight-medium mt-3"
                >
                  Not Available
                </v-chip>

                <v-chip
                  v-else
                  small
                  color="white"
                  text-color="success"
                  class="font-weight-bold mt-3 elevation-1"
                >
                  Selected
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-divider class="my-4" />

      <!-- Footer Actions -->
      <v-card-actions class="justify-space-between flex-wrap">
        <v-spacer />

        <v-dialog max-width="320">
          <template v-slot:activator="{ on, attrs }">
            <v-btn text color="error" v-bind="attrs" v-on="on">
              Cancel Subscription
            </v-btn>
          </template>

          <v-card>
            <v-card-title class="headline">Cancel Account</v-card-title>
            <v-card-text>
              To cancel this entire subscription, please email us at
              <strong>{{ clientEmail }}</strong
              >. <br /><br />
              If you would like to stop charges, you may switch to the
              <strong>Free Plan</strong> instead.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text color="primary" @click="$emit('cancelDialog', false)">
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import locationService from "../service";
import publicService from "../../../api/public";
import Loading from "../../../components/LoadingDialog";

export default {
  name: "location-billing-update-plan",
  props: ["location"],
  components: { Loading },
  data() {
    return {
      isLoading: false,
      dialog: false,
      plans: [],
      billing: null,
      clientEmail: process.env.VUE_APP_CLIENT_EMAIL,
    };
  },
  created() {
    this.fetchPlans();
    this.loadBilling();
  },
  methods: {
    cancel() {
      this.dialog = false;
    },

    async fetchPlans() {
      try {
        const response = await publicService.GetPlans();
        this.plans = response.data;
      } catch (err) {
        console.error("Error fetching plans", err);
      }
    },

    async loadBilling() {
      try {
        const response = await locationService.Billing.getInfo(
          this.location.id
        );
        this.billing = response.data;
      } catch (err) {
        console.error("Error loading billing info", err);
      }
    },

    isFreePlan(plan) {
      // adjust logic if your free plan has unique planId
      return plan.amount === 0 || plan.name.toLowerCase().includes("free");
    },

    async confirmPlanChange(plan) {
      const result = await this.$swal.fire({
        title: "Are you sure?",
        text: `You are about to change to the "${plan.name}" plan.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, change it",
      });

      if (result.isConfirmed) {
        this.checkPlan(plan.planId);
      }
    },

    async checkPlan(planId) {
      // Promo-protected plan
      if (planId === "plan_GIHbR5T1dlFaX5") {
        const { value: password } = await this.$swal.fire({
          title: "Enter Promotion Code",
          input: "password",
          inputLabel: "Password",
          inputPlaceholder: "Code",
          inputAttributes: {
            maxlength: 10,
            autocapitalize: "off",
            autocorrect: "off",
          },
        });

        if (password === "setup2312141") {
          this.changePlan(planId);
        } else {
          this.$swal.fire("Invalid promotion code, please try again.");
        }
      } else {
        this.changePlan(planId);
      }
    },

    async changePlan(planId) {
      this.isLoading = true;
      this.$Progress.start();

      try {
        await locationService.Billing.changePlan(this.location.id, { planId });
        this.dialog = false;
        await this.loadBilling();
        this.$swal.fire({
          toast: true,
          position: "top-end",
          timer: 3000,
          icon: "success",
          title: "Plan was changed successfully.",
        });
        this.$emit("updated");
      } catch (error) {
        this.$swal.fire({
          toast: true,
          position: "top-end",
          timer: 3000,
          icon: "error",
          title: error.message || error.data.message,
        });
      } finally {
        this.isLoading = false;
        this.$Progress.finish();
      }
    },
  },
};
</script>

<style scoped>
.plan-card {
  position: relative;
  transition: all 0.25s ease;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
}
.plan-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}
.active-plan {
  border: 2px solid #43a047 !important;
  background: linear-gradient(145deg, #f0fff4, #ffffff);
  box-shadow: 0 0 12px rgba(67, 160, 71, 0.25);
}
.current-chip {
  border-radius: 20px;
  box-shadow: 0 2px 6px rgba(67, 160, 71, 0.25);
}
</style>
